import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import type { FC } from "react";
import { DiffableField } from "../diffableField.js";

interface OutcomeFieldsProps {
  outcome: OutcomeDefinition | null;
  /**
   * Baseline context to diff the current context against (live/published in
   * normal editing, the current outcome while previewing an agent suggestion).
   * When it differs from the displayed context, a "See changes" toggle appears.
   */
  diffBaselineContext?: string | null;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
  /**
   * Fill a bounded parent (the assessment editor's settings pane) with a
   * context field that scrolls internally. Pass `false` on a flowing page so
   * the field takes a fixed height and the page is the only scroll region.
   * @default true
   */
  fillsHeight?: boolean;
}

/**
 * Renders the AI instructions for a transcript-derived outcome.
 */
export const OutcomeFields: FC<OutcomeFieldsProps> = (props) => {
  const {
    outcome,
    diffBaselineContext,
    onUpdateOutcome,
    isReadOnly,
    fillsHeight = true,
  } = props;

  if (!outcome) {
    return null;
  }

  return (
    <div className={fillsHeight ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <DiffableField
        label={<Label size={Size.SMALL}>Instructions</Label>}
        value={outcome.context}
        onChange={(context) => {
          onUpdateOutcome({ ...outcome, context });
        }}
        originalValue={diffBaselineContext ?? undefined}
        placeholder="Describe how Alpharun should determine the outcome from the transcript..."
        isReadOnly={isReadOnly}
        editorClassName={fillsHeight ? "min-h-0 flex-1" : "h-[400px]"}
      />
    </div>
  );
};
